import * as THREE from 'three';

/**
 * Generates procedural high-detail 3D showcase models with dynamic LOD decimation steps (1 to 8 levels).
 * This ensures the showcase website works immediately out-of-the-box without requiring external asset downloads.
 */

export function createProceduralModel(modelId, lodLevel = 0, totalLods = 6) {
  const group = new THREE.Group();
  group.name = `ProceduralModel_${modelId}_LOD${lodLevel}`;

  // Reduction ratio: LOD 0 is 1.0 (100%), LOD N decays down to ~5%
  const factor = Math.max(0.05, Math.pow(0.55, lodLevel));

  let primaryColor = 0x00f3ff;
  let secondaryColor = 0x7928ca;
  let roughness = 0.3;
  let metalness = 0.8;

  if (modelId === 'sci_fi_ship') {
    primaryColor = 0xff3b30;
    secondaryColor = 0x1c1c1e;
    metalness = 0.9;
  } else if (modelId === 'ancient_statue') {
    primaryColor = 0xe5e5ea;
    secondaryColor = 0x8e8e93;
    roughness = 0.7;
    metalness = 0.1;
  }

  // Base resolution segments depending on LOD factor
  const baseRes = Math.max(4, Math.floor(48 * factor));
  const heightRes = Math.max(4, Math.floor(32 * factor));

  const material = new THREE.MeshStandardMaterial({
    color: primaryColor,
    roughness: roughness,
    metalness: metalness,
    flatShading: lodLevel > 3 // Flat shaded for extreme low LODs to highlight low poly decimation
  });

  const secondaryMaterial = new THREE.MeshStandardMaterial({
    color: secondaryColor,
    roughness: roughness + 0.1,
    metalness: metalness,
    flatShading: lodLevel > 3
  });

  if (modelId === 'cyber_mech') {
    // Central torso
    const torsoGeo = new THREE.CylinderGeometry(0.8, 1.2, 2.2, baseRes, heightRes);
    const torsoMesh = new THREE.Mesh(torsoGeo, material);
    torsoMesh.position.y = 1.6;
    group.add(torsoMesh);

    // Head unit
    const headGeo = new THREE.IcosahedronGeometry(0.7, Math.max(0, Math.floor(4 * factor)));
    const headMesh = new THREE.Mesh(headGeo, secondaryMaterial);
    headMesh.position.y = 3.2;
    group.add(headMesh);

    // Shoulders & Arms
    if (lodLevel < totalLods - 1) {
      const armRes = Math.max(3, Math.floor(16 * factor));
      const armGeo = new THREE.CylinderGeometry(0.25, 0.2, 1.8, armRes);
      
      const leftArm = new THREE.Mesh(armGeo, secondaryMaterial);
      leftArm.position.set(-1.6, 1.6, 0);
      leftArm.rotation.z = 0.3;
      group.add(leftArm);

      const rightArm = new THREE.Mesh(armGeo, secondaryMaterial);
      rightArm.position.set(1.6, 1.6, 0);
      rightArm.rotation.z = -0.3;
      group.add(rightArm);
    }

    // Legs
    const legGeo = new THREE.BoxGeometry(0.5 * Math.max(0.8, factor), 1.8, 0.5 * Math.max(0.8, factor), Math.max(1, Math.floor(8 * factor)), Math.max(1, Math.floor(8 * factor)));
    const leftLeg = new THREE.Mesh(legGeo, material);
    leftLeg.position.set(-0.7, -0.2, 0);
    group.add(leftLeg);

    const rightLeg = new THREE.Mesh(legGeo, material);
    rightLeg.position.set(0.7, -0.2, 0);
    group.add(rightLeg);

  } else if (modelId === 'sci_fi_ship') {
    // Fuselage
    const coneGeo = new THREE.ConeGeometry(1.2, 4.5, baseRes, heightRes);
    const fuselage = new THREE.Mesh(coneGeo, material);
    fuselage.rotation.x = Math.PI / 2;
    group.add(fuselage);

    // Wings
    const wingGeo = new THREE.BoxGeometry(4.2 * factor, 0.1, 1.8, Math.max(1, Math.floor(12 * factor)), 1, Math.max(1, Math.floor(8 * factor)));
    const wings = new THREE.Mesh(wingGeo, secondaryMaterial);
    wings.position.set(0, 0, -0.5);
    group.add(wings);

    // Cockpit
    const cockpitGeo = new THREE.SphereGeometry(0.6, baseRes, heightRes);
    const cockpitMat = new THREE.MeshStandardMaterial({
      color: 0x00f3ff,
      roughness: 0.1,
      metalness: 0.9,
      transparent: true,
      opacity: 0.8
    });
    const cockpit = new THREE.Mesh(cockpitGeo, cockpitMat);
    cockpit.position.set(0, 0.4, 0.5);
    cockpit.scale.set(1, 0.8, 1.6);
    group.add(cockpit);

  } else {
    // Sculpted Bust / Organic
    const detail = Math.max(0, Math.floor(6 * factor));
    const bustGeo = new THREE.TorusKnotGeometry(1.0, 0.35, Math.max(12, Math.floor(128 * factor)), Math.max(4, Math.floor(32 * factor)));
    const bustMesh = new THREE.Mesh(bustGeo, material);
    bustMesh.position.y = 1.2;
    group.add(bustMesh);

    const pedestalGeo = new THREE.CylinderGeometry(1.2, 1.4, 0.8, Math.max(6, Math.floor(32 * factor)));
    const pedestal = new THREE.Mesh(pedestalGeo, secondaryMaterial);
    pedestal.position.y = -0.4;
    group.add(pedestal);
  }

  return group;
}

/**
 * Creates a single GLB scene containing all LOD levels as child nodes (LOD0, LOD1, LOD2... LOD N).
 */
export function createSingleGlbContainer(lodCount = 6) {
  const container = new THREE.Group();
  container.name = "SingleGLB_RootContainer";

  for (let i = 0; i < lodCount; i++) {
    const lodNode = createProceduralModel('cyber_mech', i, lodCount);
    lodNode.name = `LOD${i}`;
    lodNode.visible = (i === 0); // Only LOD0 visible by default
    container.add(lodNode);
  }

  return container;
}
